import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../../context/StoryContext';
import { generateMonthlyIdeas } from '../../services/aiService';

import { generateInitialPrompt } from '../../services/aiService';

const StoryIdeas = () => {
  const navigate = useNavigate();
  const { state, setStoryIdeas, updateWorld, archiveCurrentStory, startOverChapter, addStoryEntry, setGenerating } = useStory();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchIdeas = async () => {
    setLoading(true);
    const ideas = await generateMonthlyIdeas(state.world.genre, state.character, state.ai.apiKey, state.ai.model);
    setStoryIdeas(ideas);
    setLoading(false);
  };

  useEffect(() => {
    if (!state.storyIdeas || state.storyIdeas.length === 0) fetchIdeas();
  }, []);

  const themeColors = {
    'Power & Legitimacy': 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400',
    'Mystery & Discovery': 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
    'Duty & Sacrifice': 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
    'Survival & Hope': 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
    'Identity & Ethics': 'bg-gray-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400',
    default: 'bg-gray-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400',
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest">
          {new Date().toLocaleString('default', { month: 'long' })} Story Sparks
        </h4>
        <button onClick={fetchIdeas} disabled={loading} className="p-1.5 bg-amber-500/10 text-amber-600 rounded-lg hover:scale-110 transition-transform">
          <SafeIcon icon={loading ? FiIcons.FiLoader : FiIcons.FiRefreshCw} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="space-y-3">
        {(state.storyIdeas || []).map((idea, idx) => (
          <motion.div key={idea.id || idx} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }}>
            <button
              onClick={() => setSelected(selected === idx ? null : idx)}
              className={`w-full p-4 rounded-xl border text-left transition-all ${selected === idx ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800' : 'bg-white dark:bg-purple-900/40 border-gray-100 dark:border-purple-800 hover:border-purple-300'}`}
            >
              <div className="flex justify-between items-start mb-1">
                <h5 className="text-xs font-bold text-gray-900 dark:text-white">{idea.title}</h5>
                <span className={`text-[8px] font-black px-2 py-0.5 rounded-full ${themeColors[idea.theme] || themeColors.default}`}>
                  {idea.theme?.split(' & ')[0]}
                </span>
              </div>
              <AnimatePresence>
                {selected === idx && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <p className="text-[10px] text-gray-900 dark:text-purple-300 mt-2 leading-relaxed italic">"{idea.hook}"</p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-[9px] text-amber-600 font-bold uppercase">Theme: {idea.theme}</p>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const newWorld = { ...state.world, setting: idea.hook };
                          updateWorld({ setting: idea.hook });

                          if (state.story && state.story.length > 0) {
                            await archiveCurrentStory(idea.title || 'Spark Chapter', idea.hook);
                          } else if (startOverChapter) {
                            startOverChapter();
                          }

                          navigate('/story');
                          setGenerating(true);
                          try {
                            const initialText = await generateInitialPrompt(newWorld, state.character, state.narrativeStyle, state.ai.settings, state.ai.apiKey, state.ai.model);
                            addStoryEntry({ role: 'ai', content: initialText, timestamp: new Date().toISOString() });
                          } finally {
                            setGenerating(false);
                          }
                        }}
                        className="flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-[10px] px-3 py-1 rounded-full font-bold transition-colors"
                      >
                        Begin Spark <SafeIcon icon={FiIcons.FiArrowRight} />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default StoryIdeas;
